import { useNavigate } from '@tanstack/react-router'
import { createContext, useCallback, useContext, useMemo } from 'react'

/**
 * Workspace abstraction.
 *
 * Features open through `workspace.open({ module })` and never touch the
 * router. Site Mode translates that to navigation; PC Mode translates the
 * identical call to a window on the desktop (F138).
 *
 * It existed with one method and one module from F002, because the cost of
 * adding it later is every feature written in between. That bet paid twice:
 * PC Mode arrived twenty-two features later as native windows (F004), and
 * again as windows inside Aeris (F138), and both times changed this file and
 * no screen. docs/UI_UX.md is explicit that a feature must not care which
 * shell is displaying it, and none of them do.
 *
 * The window state — where, how big, which one is in front — lives in
 * `lib/desktop/window-store.ts`, not here: this file says WHAT to open.
 */

/** Modules that exist. Business modules are added by their own features. */
export type WorkspaceModule =
  | 'inicio'
  | 'ajuda'
  | 'produtos'
  | 'importar'
  | 'orcamentos'
  | 'entregas'
  | 'rotas'
  | 'ponto'
  | 'producao'
  | 'impressoras'
  | 'equipe-producao'
  | 'importar-clientes'
  | 'importar-fotos'
  | 'pessoas'
  | 'estoque'
  | 'precos'
  | 'pdv'
  | 'vendas'
  | 'compras'
  | 'financeiro'
  | 'cobrancas'
  | 'comissoes'
  | 'relatorios'
  | 'gastos'
  | 'pix'
  | 'importar-nfe'
  | 'empresa'
  | 'caixa'
  | 'etiquetas'
  | 'trocas'
  | 'marcadores'
  | 'marcas'
  | 'modelo-de-etiqueta'
  | 'modelo-de-nota'
  | 'aparencia'
  | 'loja-online'
  | 'nota-fiscal'
  | 'notas-fiscais'
  | 'assistente'
  | 'meu-assistente'
  | 'formas-de-pagamento'
  | 'terminais'
  | 'canais'
  | 'pedidos-canal'
  | 'pedidos-loja'
  | 'aplicacoes'
  | 'equipe'
  | 'edge'
  | 'baixar'
  | 'atendimento-config'
  | 'atendimento-canais'
  | 'atendimento-guia'
  | 'atendimento-grupos'
  | 'atendimento-filas'
  | 'atendimento-modelos'
  | 'atendimento-robo'
  | 'atendimento-robo-preco'
  | 'atendimento-pos-venda'
  | 'atendimento'
  | 'chamados'
  | 'crm-regras'
  | 'crm-campanhas'
  | 'atendimento-custos'
  | 'atendimento-painel'
  | 'configuracoes'

const MODULE_ROUTES: Record<WorkspaceModule, string> = {
  inicio: '/inicio',
  ajuda: '/ajuda',
  produtos: '/produtos',
  orcamentos: '/orcamentos',
  entregas: '/entregas',
  rotas: '/rotas',
  ponto: '/ponto',
  producao: '/producao',
  impressoras: '/impressoras',
  'equipe-producao': '/equipe-producao',
  importar: '/importar',
  'importar-clientes': '/importar-clientes',
  'importar-fotos': '/importar-fotos',
  pessoas: '/pessoas',
  estoque: '/estoque',
  precos: '/precos',
  pdv: '/pdv',
  vendas: '/vendas',
  compras: '/compras',
  financeiro: '/financeiro',
  cobrancas: '/cobrancas',
  comissoes: '/comissoes',
  relatorios: '/relatorios',
  gastos: '/gastos',
  pix: '/pix',
  'importar-nfe': '/importar-nfe',
  empresa: '/empresa',
  caixa: '/caixa',
  etiquetas: '/etiquetas',
  trocas: '/trocas',
  marcadores: '/marcadores',
  marcas: '/marcas',
  'modelo-de-etiqueta': '/modelo-de-etiqueta',
  'modelo-de-nota': '/modelo-de-nota',
  aparencia: '/aparencia',
  'loja-online': '/loja-online',
  'nota-fiscal': '/nota-fiscal',
  'notas-fiscais': '/notas-fiscais',
  assistente: '/assistente',
  'meu-assistente': '/meu-assistente',
  'formas-de-pagamento': '/formas-de-pagamento',
  terminais: '/terminais',
  canais: '/canais',
  'pedidos-canal': '/pedidos-canal',
  /** O pedido da loja online, para a vendedora aprovar (F224). */
  'pedidos-loja': '/pedidos-loja',
  aplicacoes: '/aplicacoes',
  equipe: '/equipe',
  edge: '/edge',
  baixar: '/baixar',
  /*
   * As duas páginas de configuração (F068).
   *
   * Elas não desenham conteúdo: mandam para a primeira aba que a pessoa
   * pode abrir. As abas continuam nos caminhos que sempre tiveram —
   * `/atendimento-canais`, `/empresa` — porque `page-access.test.ts` exige
   * `moduleForPath('/${module}') === module` para toda entrada de `PAGES`,
   * e mover as URLs teria custado a permissão por tela de cada uma.
   */
  'atendimento-config': '/atendimento-config',
  configuracoes: '/configuracoes',
  /*
   * Achatada, como `importar-clientes` e `pedidos-canal`: `moduleForPath`
   * resolve `/${module}` para o próprio módulo (page-access.test.ts), então
   * uma rota aninhada `/atendimento/canais` quebraria essa garantia para
   * TODO módulo do catálogo, não só este. As telas seguintes do módulo
   * (`atendimento-filas`, `atendimento-robo`…) seguem o mesmo padrão.
   */
  'atendimento-canais': '/atendimento-canais',
  /** O guia de configuração (F055) — mesmo padrão achatado. */
  'atendimento-guia': '/atendimento-guia',
  /** Filas, membros e horário (F047) — mesmo padrão achatado. */
  'atendimento-grupos': '/atendimento-grupos',
  'atendimento-filas': '/atendimento-filas',
  /** Modelos aprovados pela Meta, e a janela de 24h (F050) — mesmo padrão achatado. */
  'atendimento-modelos': '/atendimento-modelos',
  /** O robô de IA: config, provedores, credenciais, sessões (F052) — mesmo padrão achatado. */
  'atendimento-robo': '/atendimento-robo',
  /** O robô de preço (F193) — mesmo padrão achatado. */
  'atendimento-robo-preco': '/atendimento-robo-preco',
  /** O "saiu para entrega" e o pós-venda (F245/F246) — mesmo padrão achatado. */
  'atendimento-pos-venda': '/atendimento-pos-venda',
  /*
   * O inbox (F046). `moduleForPath` casa por `pathname === route` ou
   * `pathname.startsWith(route + '/')` (page-access.test.ts exige
   * `moduleForPath('/${module}') === module`), então `/atendimento-canais`
   * não confunde com esta rota: depois de `/atendimento` o próximo caractere
   * é `-`, não `/`.
   */
  atendimento: '/atendimento',
  /** Chamados (F051): protocolo, situação, prioridade, responsável — não ordem de serviço técnica. Achatada, mesmo padrão. */
  chamados: '/chamados',
  /** Regras de follow-up (F053): "comprou ar, lembrar da manutenção em 6 meses" — mesmo padrão achatado. */
  'crm-regras': '/crm-regras',
  /** Campanhas (F054): "pegar todos os clientes que compraram X e mandar mensagem de reajuste" — mesmo padrão achatado. */
  'crm-campanhas': '/crm-campanhas',
  /** Custos de atendimento (F054): a conta no fim do mês — mesmo padrão achatado. */
  'atendimento-custos': '/atendimento-custos',
  'atendimento-painel': '/atendimento-painel',
}

/**
 * Qual tela é esta, a partir do endereço.
 *
 * O contrário de `routeFor`, e existe porque o menu não é o único caminho até
 * uma tela: um favorito, um link colado, a janela do Modo PC e o botão de
 * voltar entram direto pela URL. Uma tela que o admin tirou da conta tem que
 * dizer isso também por essa porta.
 *
 * Prefixo mais longo primeiro: `/importar-clientes` começa com `/importar`, e
 * a ordem ingênua responderia "Importar" para as duas.
 */
/**
 * Um módulo conhecido, ou `null` — F089.
 *
 * É o que impede `?voltar=` de virar redirecionamento aberto: o valor da URL
 * nunca é usado como caminho, só como CHAVE de um mapa que este arquivo
 * declara. Qualquer coisa fora dele vira ausência, e a tela fecha o formulário
 * onde está.
 */
export const asWorkspaceModule = (value: unknown): WorkspaceModule | null =>
  /*
   * `Object.hasOwn` e NÃO `in`: `in` atravessa a cadeia de protótipos, e
   * `'toString' in MODULE_ROUTES` é `true`. Com `in`, `?voltar=toString`
   * passaria pela validação e a tela navegaria para `undefined`. Achado por
   * `workspace.test.ts`, que foi escrito para procurar exatamente isto.
   */
  typeof value === 'string' && Object.hasOwn(MODULE_ROUTES, value)
    ? (value as WorkspaceModule)
    : null

export const moduleForPath = (pathname: string): WorkspaceModule | null => {
  const matches = (Object.entries(MODULE_ROUTES) as [WorkspaceModule, string][])
    .filter(
      ([, route]) => pathname === route || pathname.startsWith(`${route}/`)
    )
    .sort(([, a], [, b]) => b.length - a.length)

  return matches[0]?.[0] ?? null
}

/**
 * What the window's title bar says.
 *
 * Kept here beside the routes rather than read from `lib/modules.tsx`, which
 * carries React components: this module is imported by the router and must
 * stay free of anything that renders.
 */
export const moduleRoute = (module: WorkspaceModule): string =>
  MODULE_ROUTES[module]

/** O título de uma janela que mostra este módulo. */
export const moduleTitle = (module: WorkspaceModule): string =>
  MODULE_TITLES[module]

const MODULE_TITLES: Record<WorkspaceModule, string> = {
  inicio: 'Início',
  ajuda: 'Ajuda',
  produtos: 'Produtos',
  orcamentos: 'Orçamentos',
  entregas: 'Acerto do motoboy',
  rotas: 'Rotas de entrega',
  ponto: 'Ponto',
  producao: 'Produção',
  impressoras: 'Impressoras da produção',
  'equipe-producao': 'Equipe da produção',
  importar: 'Importar',
  'importar-clientes': 'Importar clientes',
  'importar-fotos': 'Importar fotos',
  pessoas: 'Clientes',
  estoque: 'Estoque',
  precos: 'Preços',
  pdv: 'PDV',
  vendas: 'Vendas',
  compras: 'Compras',
  financeiro: 'Financeiro',
  cobrancas: 'Cobranças',
  comissoes: 'Comissões',
  relatorios: 'Relatórios',
  gastos: 'Folha e gastos',
  pix: 'Pix recebidos',
  'importar-nfe': 'Importar NF-e',
  empresa: 'Empresa',
  caixa: 'Caixa',
  etiquetas: 'Etiquetas',
  trocas: 'Trocas e vales',
  marcadores: 'Marcadores',
  marcas: 'Marcas',
  'modelo-de-etiqueta': 'Modelo de etiqueta',
  'modelo-de-nota': 'Modelo da nota',
  aparencia: 'Aparência',
  'loja-online': 'Loja online',
  'nota-fiscal': 'Nota fiscal',
  'notas-fiscais': 'Notas fiscais',
  assistente: 'Assistente',
  'meu-assistente': 'Meu assistente',
  'formas-de-pagamento': 'Formas de pagamento',
  terminais: 'Terminais',
  canais: 'Canais',
  'pedidos-canal': 'Pedidos dos canais',
  'pedidos-loja': 'Pedidos da loja',
  aplicacoes: 'Aplicações',
  equipe: 'Equipe',
  edge: 'Máquinas da loja',
  baixar: 'Aeris no computador',
  'atendimento-config': 'Configurações do atendimento',
  configuracoes: 'Configurações da loja',
  'atendimento-canais': 'Canais de atendimento',
  'atendimento-guia': 'Guia de configuração',
  'atendimento-grupos': 'Grupos do WhatsApp',
  'atendimento-filas': 'Filas de atendimento',
  'atendimento-modelos': 'Modelos',
  'atendimento-robo': 'Robô de atendimento',
  'atendimento-robo-preco': 'Robô de preço',
  'atendimento-pos-venda': 'Pós-venda',
  atendimento: 'Atendimento',
  chamados: 'Chamados',
  'crm-regras': 'Regras de follow-up',
  'crm-campanhas': 'Campanhas',
  'atendimento-custos': 'Custos de atendimento',
  'atendimento-painel': 'Painel do atendimento',
}

/**
 * Como cada módulo recebe "abra ESTE registro" na URL — F089.
 *
 * Só quem sabe abrir um registro aparece aqui. Um módulo ausente da tabela
 * simplesmente ignora o `entityId`, em vez de ganhar um parâmetro que a tela
 * dele não lê — que é como uma URL passa a mentir sobre o que vai acontecer.
 */
const MODULE_ENTITY_PARAM: Partial<Record<WorkspaceModule, string>> = {
  pessoas: 'editar',
  // O cliente aberto na fila de cobrança (F135): o painel e o Financeiro
  // mandam direto para ele.
  cobrancas: 'cliente',
  /*
   * Qual RELATÓRIO abrir (F158) — `?relatorio=trocas`.
   *
   * Não é um registro, é a pergunta; mas o mecanismo é o mesmo e o nome do
   * campo é o que a tela já lê. É assim que cada bloco de ranking do painel
   * manda para o relatório inteiro daquele mesmo assunto.
   */
  relatorios: 'relatorio',
  /*
   * A ficha de um pedido na fila de produção (F170) — `?pedido=`.
   *
   * É o que deixa a ficha abrir numa janela do Modo PC e ser mandada por link:
   * "olha o pedido 3226" vira um endereço, não uma instrução de onde clicar.
   */
  producao: 'pedido',
}

export type OpenOptions = {
  module: WorkspaceModule
  /**
   * O registro a abrir — hoje só `pessoas` (F089), via `?editar=`.
   *
   * Ele deixou de ser "unused today" quando o marcador `+ Cidade` do inbox
   * passou a precisar de um destino: cair na lista de Pessoas para procurar de
   * novo alguém que a tela ao lado já sabia quem era é trabalho que o produto
   * estava criando.
   */
  entityId?: string
  /**
   * Para onde voltar quando o assunto acabar — F089.
   *
   * Existe porque no Modo Site abrir outro módulo é NAVEGAR: quem clicou em
   * "+ Cidade" no meio de um atendimento saía do atendimento, e ao fechar o
   * cadastro ficava parado em Pessoas, longe da conversa que estava lendo.
   *
   * **Ignorado no Modo PC**, e não por esquecimento: lá o outro módulo abre
   * numa JANELA, a janela de origem nunca saiu do lugar, e "voltar" mandaria
   * uma segunda superfície do mesmo módulo aparecer sem ninguém ter pedido.
   */
  returnTo?: WorkspaceModule
  /**
   * Outra janela do mesmo módulo, e não a que já existe — F138.
   *
   * Só no Modo PC, e só para quem pede explicitamente (Shift+clique, "Nova
   * janela"): é o que deixa dois produtos abertos lado a lado. No Modo Site não
   * há segunda superfície, e a opção não faz nada.
   */
  newInstance?: boolean
}

/**
 * O que a área de trabalho do Modo PC recebe quando uma tela pede outra — F138.
 */
export type DesktopOpenRequest = {
  readonly module: WorkspaceModule
  /** O caminho da tela, com o registro na busca quando há um. */
  readonly href: string
  readonly title: string
  readonly newInstance: boolean
  /** O pedido nomeia um registro: a janela que já existe vai até ele. */
  readonly carriesEntity: boolean
}

export type DesktopWorkspace = {
  readonly open: (request: DesktopOpenRequest) => void
}

/**
 * A área de trabalho, quando a tela está dentro de uma — F138.
 *
 * Mora aqui, e não em `lib/desktop/`, para o grafo de módulos não fechar um
 * ciclo: o store das janelas conhece `WorkspaceModule`, e este arquivo não
 * conhece o store.
 */
export const DesktopWorkspaceContext = createContext<DesktopWorkspace | null>(
  null
)

export type Workspace = {
  open: (options: OpenOptions) => void
  routeFor: (module: WorkspaceModule) => string
}

export const useWorkspace = (): Workspace => {
  const navigate = useNavigate()
  const desktop = useContext(DesktopWorkspaceContext)

  const open = useCallback(
    ({ module, entityId, returnTo, newInstance }: OpenOptions) => {
      const path = MODULE_ROUTES[module]
      const entityParam = MODULE_ENTITY_PARAM[module]
      const carriesEntity = entityParam !== undefined && entityId !== undefined

      /*
       * PC Mode: a window, from the identical call (F138).
       *
       * A janela carrega o registro, mas nunca o "voltar": ver `returnTo`.
       */
      if (desktop !== null) {
        desktop.open({
          module,
          href: carriesEntity
            ? `${path}?${entityParam}=${encodeURIComponent(entityId)}`
            : path,
          title: MODULE_TITLES[module],
          newInstance: newInstance === true,
          carriesEntity,
        })
        return
      }

      const search: Record<string, string> = {}
      if (entityParam !== undefined && entityId !== undefined) {
        search[entityParam] = entityId
      }
      if (returnTo !== undefined) search.voltar = returnTo

      void navigate(
        Object.keys(search).length === 0 ? { to: path } : { to: path, search }
      )
    },
    [navigate, desktop]
  )

  const routeFor = useCallback(
    (module: WorkspaceModule) => MODULE_ROUTES[module],
    []
  )

  return useMemo(() => ({ open, routeFor }), [open, routeFor])
}
